import { useAddJobTask, useRemoveJobTask } from '@/domains/jobs/hooks/useJobDetail';
import { useTaskTypes } from '@/domains/settings/hooks/useJobManagement';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { ClipboardList, Lock, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Task {
  id: number;
  name: string;
  status: string;
  orderNumber?: number;
  isCategoryTask?: boolean;
}

function getStatusLabel(status: string): string {
  const s = status.toLowerCase();
  if (s === 'completed' || s === '3') return 'Completed';
  if (s === 'in-progress' || s === 'in progress' || s === '2') return 'In Progress';
  return 'Not Started';
}

function getStatusStyle(status: string): string {
  const label = getStatusLabel(status);
  if (label === 'Completed')
    return 'bg-green-50 text-green-700 border-green-600 dark:bg-green-950/30';
  if (label === 'In Progress')
    return 'bg-blue-50 text-blue-700 border-blue-600 dark:bg-blue-950/30';
  return 'bg-slate-50 text-slate-700 border-slate-400 dark:bg-slate-900/30';
}

function getStatusDot(status: string): string {
  const label = getStatusLabel(status);
  if (label === 'Completed') return 'bg-green-600';
  if (label === 'In Progress') return 'bg-blue-600';
  return 'bg-slate-400';
}

export function TasksCard({ tasks, jobId }: { tasks?: Task[]; jobId?: string }) {
  const allTasks = tasks && tasks.length > 0 ? tasks : [];
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState<Task | null>(null);

  const numericJobId = Number(jobId);
  const addTaskMutation = useAddJobTask(numericJobId);
  const removeTaskMutation = useRemoveJobTask(numericJobId);
  const { data: taskTypesData } = useTaskTypes();
  const allTaskTypes = taskTypesData?.data ?? [];

  const existingTaskNames = useMemo(
    () => new Set(allTasks.map((t) => t.name.toLowerCase())),
    [allTasks],
  );

  const availableTaskTypes = useMemo(() => {
    let pool = allTaskTypes.filter((tt) => !existingTaskNames.has((tt.name ?? '').toLowerCase()));
    if (taskSearch) {
      const q = taskSearch.toLowerCase();
      pool = pool.filter((tt) => tt.name?.toLowerCase().includes(q));
    }
    return pool;
  }, [allTaskTypes, existingTaskNames, taskSearch]);

  async function handleAddTask(taskTypeId: number) {
    const nextOrder =
      allTasks.length > 0 ? Math.max(...allTasks.map((t) => t.orderNumber ?? 0)) + 1 : 1;
    await addTaskMutation.mutateAsync({ taskTypeId, orderNumber: nextOrder });
    showSuccessToast('Task added');
    setAddDialogOpen(false);
    setTaskSearch('');
  }

  async function handleRemoveTask() {
    if (!removeConfirm) return;
    try {
      await removeTaskMutation.mutateAsync(removeConfirm.id);
      showSuccessToast('Task removed');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? err?.message ?? 'Failed to remove task';
      showErrorToast(msg);
    }
    setRemoveConfirm(null);
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Tasks</h3>
            <p className="text-xs text-muted-foreground">
              {allTasks.length > 0
                ? `${allTasks.length} task${allTasks.length !== 1 ? 's' : ''}`
                : 'No tasks assigned'}
            </p>
          </div>
        </div>
        {jobId && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setTaskSearch('');
              setAddDialogOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Task
          </Button>
        )}
      </div>

      {allTasks.length > 0 && (
        <div className="space-y-2">
          {allTasks.map((task: Task) => (
            <div
              key={task.id}
              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <span className="text-xs font-medium text-muted-foreground w-6 text-center">
                  {task.orderNumber ?? '-'}
                </span>
                <div className={`w-2 h-2 rounded-full ${getStatusDot(task.status)}`} />
                <span className="text-sm font-medium text-foreground flex-1">{task.name}</span>
              </div>
              <div className="flex items-center gap-2 ml-3">
                <Badge variant="outline" className={getStatusStyle(task.status)}>
                  {getStatusLabel(task.status)}
                </Badge>
                {task.isCategoryTask ? (
                  <span className="relative group flex items-center justify-center h-7 w-7">
                    <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="absolute bottom-full right-0 mb-1.5 px-2.5 py-1.5 text-xs text-white bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 rounded-md shadow-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                      Mandatory — assigned from job category
                    </span>
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => setRemoveConfirm(task)}
                    title="Remove task"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Task Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search task types..."
                value={taskSearch}
                onChange={(e) => setTaskSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              {availableTaskTypes.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground text-center">
                  {taskSearch
                    ? 'No task types match your search'
                    : 'All task types already assigned'}
                </div>
              ) : (
                availableTaskTypes.map((tt) => (
                  <button
                    key={tt.id}
                    type="button"
                    onClick={() => handleAddTask(tt.id)}
                    disabled={addTaskMutation.isPending}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 text-left text-sm border-b last:border-b-0 transition-colors"
                  >
                    <ClipboardList className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-foreground">{tt.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={!!removeConfirm} onOpenChange={() => setRemoveConfirm(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Remove Task</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to remove{' '}
            <span className="font-medium text-foreground">{removeConfirm?.name}</span> from this
            job?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveTask}
              disabled={removeTaskMutation.isPending}
            >
              {removeTaskMutation.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
